import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../allauth/auth';
import ApiClient from '../config/ApiClient';
import { PaginatedResponse, PaymentAssignments } from '../object-actions/types/types';
import { ASSIGNMENTS_INVALIDATE_EVENT } from '../subscriptions/checkoutSyncEvents';

export function usePayerAssignments() {
  const auth = useAuth();
  const userId = auth?.data?.user?.id;
  const [assignments, setAssignments] = useState<PaymentAssignments[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssignments = useCallback(async (): Promise<PaymentAssignments[]> => {
    if (!userId) {
      setAssignments([]);
      setLoading(false);
      setError('User not authenticated');
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        payments__author: userId.toString(),
        manage: 'true',
        limit: '50',
      });
      const response = await ApiClient.get<PaginatedResponse<PaymentAssignments>>(
        `/api/payment-assignments/?${params.toString()}`,
      );

      if (response.error) {
        setError(response.error);
        setAssignments([]);
        return [];
      }
      if (response.success && response.data) {
        const results = response.data.results ?? [];
        setAssignments(results);
        return results;
      }
      setAssignments([]);
      return [];
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
      setAssignments([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onInvalidate = () => {
      void fetchAssignments();
    };
    window.addEventListener(ASSIGNMENTS_INVALIDATE_EVENT, onInvalidate);
    return () => {
      window.removeEventListener(ASSIGNMENTS_INVALIDATE_EVENT, onInvalidate);
    };
  }, [fetchAssignments]);

  return { assignments, loading, error, refetch: fetchAssignments };
}
